"use client";

import { AuraPresence } from "./aura-presence";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Circle,
  GitBranch,
  Server,
  Database,
  Cpu,
} from "lucide-react";
import type { OperationalStateContract } from "@/src/data/operational-state";

interface StatusIndicatorProps {
  label: string;
  value: string;
  status?: "verified" | "stable" | "synced" | "warning" | "critical" | "default";
  icon?: React.ReactNode;
}

function StatusIndicator({
  label,
  value,
  status = "default",
  icon,
}: StatusIndicatorProps) {
  const statusColors = {
    verified: "text-success",
    stable: "text-accent",
    synced: "text-accent",
    warning: "text-warning",
    critical: "text-destructive",
    default: "text-muted-foreground",
  };

  return (
    <div className="flex items-center gap-2 px-3 py-1 whitespace-nowrap">
      {icon && <span className="text-muted-foreground">{icon}</span>}
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("text-xs font-medium", statusColors[status])}>
        {value}
      </span>
    </div>
  );
}

interface TopOperationalBarProps {
  topBar: OperationalStateContract["topBar"];
  userInitials: string;
}

export function TopOperationalBar({ topBar, userInitials }: TopOperationalBarProps) {
  return (
    <header className="h-12 shrink-0 border-b border-border bg-card/80 backdrop-blur-sm flex items-center justify-between px-3 sm:px-4">
      {/* Left: Brand */}
      <div className="flex min-w-0 items-center gap-3">
        <AuraPresence size="sm" intensity="subtle" />
        <div className="flex min-w-0 items-center gap-2">
          <span className="max-w-[6.5rem] truncate text-sm font-semibold tracking-tight text-foreground sm:max-w-none">
            CLIMB ONE
          </span>
          <span className="hidden text-muted-foreground sm:inline">/</span>
          <span className="hidden text-sm font-medium text-accent sm:inline">AURA</span>
        </div>
      </div>

      {/* Center: Status Indicators */}
      <div className="hidden items-center divide-x divide-border xl:flex">
        <StatusIndicator
          label={topBar.environment.label}
          value={topBar.environment.value}
          status={topBar.environment.status}
          icon={<Server className="w-3.5 h-3.5" />}
        />
        <StatusIndicator
          label={topBar.runtime.label}
          value={topBar.runtime.value}
          status={topBar.runtime.status}
          icon={<Cpu className="w-3.5 h-3.5" />}
        />
        <StatusIndicator
          label={topBar.memory.label}
          value={topBar.memory.value}
          status={topBar.memory.status}
          icon={<Database className="w-3.5 h-3.5" />}
        />
        <StatusIndicator
          label={topBar.provider.label}
          value={topBar.provider.value}
          status={topBar.provider.status}
          icon={<Circle className="w-3.5 h-3.5" />}
        />
        <StatusIndicator
          label={topBar.branch.label}
          value={topBar.branch.value}
          status={topBar.branch.status}
          icon={<GitBranch className="w-3.5 h-3.5" />}
        />
        <StatusIndicator
          label={topBar.health.label}
          value={topBar.health.value}
          status={topBar.health.status}
          icon={<CheckCircle2 className="w-3.5 h-3.5" />}
        />
      </div>

      {/* Right: User/Settings placeholder */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-muted/30 px-2.5 py-1 text-[11px] text-muted-foreground xl:hidden">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          <span className="truncate">{topBar.runtime.value}</span>
          <span className="text-border">/</span>
          <span className="truncate text-accent">{topBar.health.value}</span>
        </div>
        <div className="w-7 h-7 rounded-full bg-muted border border-border flex items-center justify-center">
          <span className="text-xs text-muted-foreground">{userInitials}</span>
        </div>
      </div>
    </header>
  );
}
